import { useResendCountdown } from "@babackapp/auth-client/hooks";
import type { MfaEmailCodeSent, MfaMethod } from "@babackapp/auth-client/types";
import {
	hasActiveSuperpower,
	receiveMfaEmailCode,
} from "@babackapp/auth-client/utils";
import { useConfirmEmailMfaEnrollment } from "@babackapp/private-api-client/client/use-confirm-email-mfa-enrollment";
import { useConfirmTotpEnrollment } from "@babackapp/private-api-client/client/use-confirm-totp-enrollment";
import { useCurrentUser } from "@babackapp/private-api-client/client/use-current-user";
import { useDisableMfa } from "@babackapp/private-api-client/client/use-disable-mfa";
import { useRegenerateMfaRecoveryCodes } from "@babackapp/private-api-client/client/use-regenerate-mfa-recovery-codes";
import { useSendMfaSettingsEmailCode } from "@babackapp/private-api-client/client/use-send-mfa-settings-email-code";
import { useStartEmailMfaEnrollment } from "@babackapp/private-api-client/client/use-start-email-mfa-enrollment";
import { useStartTotpEnrollment } from "@babackapp/private-api-client/client/use-start-totp-enrollment";
import type { DisableMfaRequestDto } from "@babackapp/private-api-client/types";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { Loader2 } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { MfaRecoveryCodes } from "./mfa-recovery-codes";

const SIX_DIGIT_CODE_PATTERN = /^\d{6}$/;

function getErrorMessage(error: unknown): string | null {
	if (
		error &&
		typeof error === "object" &&
		"message" in error &&
		typeof error.message === "string"
	) {
		return error.message;
	}
	return null;
}

function ErrorMessage({ error }: { error: unknown }) {
	const message = getErrorMessage(error);
	return message ? <p className="text-sm text-destructive">{message}</p> : null;
}

function SixDigitCodeInput({
	id,
	value,
	onChange,
}: {
	id: string;
	value: string;
	onChange: (value: string) => void;
}) {
	return (
		<Input
			id={id}
			value={value}
			onChange={(event) =>
				onChange(event.target.value.replace(/\D/g, "").slice(0, 6))
			}
			inputMode="numeric"
			autoComplete="one-time-code"
			className="w-40"
			autoFocus
		/>
	);
}

function ResendButton({
	codeSent,
	isPending,
	onResend,
}: {
	codeSent: MfaEmailCodeSent;
	isPending: boolean;
	onResend: () => void;
}) {
	const { t } = useTranslation("frontend");
	const secondsBeforeResend = useResendCountdown(codeSent);

	return (
		<button
			type="button"
			onClick={onResend}
			disabled={isPending || secondsBeforeResend > 0}
			className="w-fit text-sm font-medium text-primary hover:text-primary/80 disabled:text-muted-foreground"
		>
			{secondsBeforeResend > 0
				? t("mfa.email.resendIn", { seconds: secondsBeforeResend })
				: t("mfa.email.resend")}
		</button>
	);
}

/** A code from a method already enabled. */
interface Proof {
	method: MfaMethod;
	value: string;
}

function toMfaProof({
	method,
	value,
}: Proof): Pick<DisableMfaRequestDto, "code" | "recoveryCode" | "emailCode"> {
	const trimmed = value.trim();
	if (method === "email") {
		return { emailCode: trimmed };
	}
	return SIX_DIGIT_CODE_PATTERN.test(trimmed)
		? { code: trimmed }
		: { recoveryCode: trimmed };
}

/**
 * The code input for one of `methods`, with a switch between them. The last
 * email sent stays in local state: a new `mutate` clears the mutation's data.
 */
function ProofField({
	id,
	methods,
	proof,
	onChange,
}: {
	id: string;
	methods: MfaMethod[];
	proof: Proof;
	onChange: (proof: Proof) => void;
}) {
	const { t } = useTranslation("frontend");
	const sendEmailCode = useSendMfaSettingsEmailCode();
	const [codeSent, setCodeSent] = useState<MfaEmailCodeSent | null>(null);

	const send = () =>
		void sendEmailCode
			.mutateAsync()
			.then((response) => setCodeSent(receiveMfaEmailCode(response)))
			.catch(() => undefined);

	const otherMethod = methods.find((method) => method !== proof.method);

	return (
		<div className="flex flex-col gap-3">
			{proof.method === "totp" ? (
				<>
					<Label htmlFor={id}>{t("accountSettings.mfa.codeOrRecoveryCode")}</Label>
					<Input
						id={id}
						value={proof.value}
						onChange={(event) => onChange({ ...proof, value: event.target.value })}
						autoComplete="one-time-code"
						className="w-56"
					/>
				</>
			) : codeSent ? (
				<>
					<p className="text-sm">
						{t("mfa.email.codeSent", { email: codeSent.maskedEmail })}
					</p>
					<Label htmlFor={id}>{t("mfa.email.codeLabel")}</Label>
					<SixDigitCodeInput
						id={id}
						value={proof.value}
						onChange={(value) => onChange({ ...proof, value })}
					/>
					<ResendButton
						codeSent={codeSent}
						isPending={sendEmailCode.isPending}
						onResend={send}
					/>
				</>
			) : (
				<Button
					type="button"
					variant="outline"
					className="w-fit"
					disabled={sendEmailCode.isPending}
					onClick={send}
				>
					{t("accountSettings.mfa.proof.sendEmail")}
				</Button>
			)}
			<ErrorMessage error={sendEmailCode.error} />
			{otherMethod && (
				<button
					type="button"
					onClick={() => onChange({ method: otherMethod, value: "" })}
					className="w-fit text-sm font-medium text-primary hover:text-primary/80"
				>
					{otherMethod === "email"
						? t("accountSettings.mfa.proof.useEmail")
						: t("accountSettings.mfa.proof.useApp")}
				</button>
			)}
		</div>
	);
}

interface RecoveryCodesState {
	codes: string[];
	isAfterEnrollment: boolean;
}

/** Texts live in the `frontend` namespace, which both dashboards load. */
export function MfaSettings() {
	const { t } = useTranslation("frontend");
	const { data: user, isLoading } = useCurrentUser();
	const [recoveryCodes, setRecoveryCodes] =
		useState<RecoveryCodesState | null>(null);
	const [isEmailJustEnabled, setIsEmailJustEnabled] = useState(false);

	if (isLoading || !user) {
		return <Loader2 className="size-4 animate-spin" />;
	}

	// mfaEnabled alone comes from an API older than the email method.
	const methods: MfaMethod[] =
		user.mfaMethods ?? (user.mfaEnabled ? ["totp"] : []);
	const isTotpEnabled = methods.includes("totp");
	const isEmailEnabled = methods.includes("email");
	// A super admin keeps a second factor: the API refuses to remove the last
	// one (BAB-3035), so it is not offered.
	const isLastMethodLocked = methods.length === 1 && hasActiveSuperpower(user);

	if (recoveryCodes) {
		return (
			<MfaRecoveryCodes
				codes={recoveryCodes.codes}
				isAfterEnrollment={recoveryCodes.isAfterEnrollment}
				onDone={() => setRecoveryCodes(null)}
			/>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<p className="text-sm text-muted-foreground">
				{t("accountSettings.mfa.methodsDescription")}
			</p>

			<MfaMethodSection
				title={t("accountSettings.mfa.totp.title")}
				description={t("accountSettings.mfa.totp.description")}
				isEnabled={isTotpEnabled}
			>
				{isTotpEnabled ? (
					<TotpEnabledActions
						methods={methods}
						isDisableLocked={isLastMethodLocked}
						onRecoveryCodes={(codes) =>
							setRecoveryCodes({ codes, isAfterEnrollment: false })
						}
					/>
				) : (
					<TotpEnrollment
						onEnabled={(codes) =>
							setRecoveryCodes({ codes, isAfterEnrollment: true })
						}
					/>
				)}
			</MfaMethodSection>

			<MfaMethodSection
				title={t("accountSettings.mfa.email.title")}
				description={t("accountSettings.mfa.email.description", {
					email: user.emails[0]?.address ?? "",
				})}
				isEnabled={isEmailEnabled}
			>
				{isEmailJustEnabled && isEmailEnabled && (
					<p className="text-sm text-green-700">
						{t("accountSettings.mfa.email.enabledNotice")}
					</p>
				)}
				{isEmailEnabled ? (
					<ToggleableDisableForm
						method="email"
						methods={methods}
						isDisableLocked={isLastMethodLocked}
					/>
				) : (
					<EmailEnrollment onEnabled={() => setIsEmailJustEnabled(true)} />
				)}
			</MfaMethodSection>
		</div>
	);
}

function MfaMethodSection({
	title,
	description,
	isEnabled,
	children,
}: {
	title: string;
	description: string;
	isEnabled: boolean;
	children: ReactNode;
}) {
	const { t } = useTranslation("frontend");

	return (
		<div className="flex flex-col gap-3 rounded border p-4">
			<div className="flex flex-col gap-1">
				<div className="flex items-center gap-2">
					<p className="text-sm font-medium">{title}</p>
					{isEnabled && (
						<Badge variant="success-outline">
							{t("accountSettings.mfa.enabled")}
						</Badge>
					)}
				</div>
				<p className="text-xs text-muted-foreground">{description}</p>
			</div>
			{children}
		</div>
	);
}

function PasswordStep({
	isPending,
	error,
	onSubmit,
	onCancel,
}: {
	isPending: boolean;
	error: unknown;
	onSubmit: (password: string) => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation("frontend");
	const [password, setPassword] = useState("");

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		onSubmit(password);
	};

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-3">
			<Label htmlFor="mfa-password">
				{t("accountSettings.mfa.passwordPrompt")}
			</Label>
			<Input
				id="mfa-password"
				type="password"
				value={password}
				onChange={(event) => setPassword(event.target.value)}
				autoComplete="current-password"
				autoFocus
			/>
			<ErrorMessage error={error} />
			<div className="flex gap-2">
				<Button type="submit" disabled={!password || isPending}>
					{isPending ? <Loader2 className="size-4 animate-spin" /> : t("misc.continue")}
				</Button>
				<Button type="button" variant="ghost" onClick={onCancel}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}

function TotpEnrollment({
	onEnabled,
}: {
	onEnabled: (recoveryCodes: string[]) => void;
}) {
	const { t } = useTranslation("frontend");
	const [isStarted, setIsStarted] = useState(false);
	const [code, setCode] = useState("");
	const start = useStartTotpEnrollment();
	const confirm = useConfirmTotpEnrollment();
	const enrollment = start.data;

	const cancel = () => {
		setIsStarted(false);
		setCode("");
		start.reset();
		confirm.reset();
	};

	if (!isStarted) {
		return (
			<Button className="w-fit" onClick={() => setIsStarted(true)}>
				{t("accountSettings.mfa.totp.enable")}
			</Button>
		);
	}

	if (!enrollment) {
		return (
			<PasswordStep
				isPending={start.isPending}
				error={start.error}
				onSubmit={(password) => start.mutate({ password })}
				onCancel={cancel}
			/>
		);
	}

	const handleConfirm = async (event: FormEvent) => {
		event.preventDefault();
		const { recoveryCodes } = await confirm.mutateAsync({ code });
		onEnabled(recoveryCodes);
	};

	return (
		<form
			onSubmit={(event) => void handleConfirm(event).catch(() => setCode(""))}
			className="flex flex-col gap-3"
		>
			<p className="text-sm">{t("accountSettings.mfa.scan")}</p>
			<img
				src={enrollment.qrCodeDataUrl}
				alt=""
				className="h-44 w-44 rounded border"
			/>
			<p className="text-xs text-muted-foreground">
				{t("accountSettings.mfa.manualKey")}{" "}
				<code className="font-mono break-all">
					{enrollment.secret.match(/.{1,4}/g)?.join(" ")}
				</code>
			</p>
			<Label htmlFor="mfa-confirm-code">{t("accountSettings.mfa.code")}</Label>
			<SixDigitCodeInput id="mfa-confirm-code" value={code} onChange={setCode} />
			<ErrorMessage error={confirm.error} />
			<div className="flex gap-2">
				<Button
					type="submit"
					disabled={!SIX_DIGIT_CODE_PATTERN.test(code) || confirm.isPending}
				>
					{confirm.isPending ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						t("accountSettings.mfa.activate")
					)}
				</Button>
				<Button type="button" variant="ghost" onClick={cancel}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}

function EmailEnrollment({ onEnabled }: { onEnabled: () => void }) {
	const { t } = useTranslation("frontend");
	const [isStarted, setIsStarted] = useState(false);
	// Kept for "resend": the setup asks for the password every time.
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	// In local state: a new `mutate` clears the mutation's data.
	const [codeSent, setCodeSent] = useState<MfaEmailCodeSent | null>(null);
	const start = useStartEmailMfaEnrollment();
	const confirm = useConfirmEmailMfaEnrollment();

	const sendCode = (typedPassword: string) =>
		void start
			.mutateAsync({ password: typedPassword })
			.then((response) => setCodeSent(receiveMfaEmailCode(response)))
			.catch(() => undefined);

	const cancel = () => {
		setIsStarted(false);
		setPassword("");
		setCode("");
		setCodeSent(null);
		start.reset();
		confirm.reset();
	};

	if (!isStarted) {
		return (
			<Button className="w-fit" onClick={() => setIsStarted(true)}>
				{t("accountSettings.mfa.email.enable")}
			</Button>
		);
	}

	if (!codeSent) {
		return (
			<PasswordStep
				isPending={start.isPending}
				error={start.error}
				onSubmit={(typedPassword) => {
					setPassword(typedPassword);
					sendCode(typedPassword);
				}}
				onCancel={cancel}
			/>
		);
	}

	const handleConfirm = async (event: FormEvent) => {
		event.preventDefault();
		await confirm.mutateAsync({ code });
		onEnabled();
	};

	return (
		<form
			onSubmit={(event) => void handleConfirm(event).catch(() => setCode(""))}
			className="flex flex-col gap-3"
		>
			<p className="text-sm">
				{t("mfa.email.codeSent", { email: codeSent.maskedEmail })}
			</p>
			<Label htmlFor="mfa-email-confirm-code">{t("mfa.email.codeLabel")}</Label>
			<SixDigitCodeInput
				id="mfa-email-confirm-code"
				value={code}
				onChange={setCode}
			/>
			<ResendButton
				codeSent={codeSent}
				isPending={start.isPending}
				onResend={() => sendCode(password)}
			/>
			<ErrorMessage error={confirm.error ?? start.error} />
			<div className="flex gap-2">
				<Button
					type="submit"
					disabled={!SIX_DIGIT_CODE_PATTERN.test(code) || confirm.isPending}
				>
					{confirm.isPending ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						t("accountSettings.mfa.activate")
					)}
				</Button>
				<Button type="button" variant="ghost" onClick={cancel}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}

function TotpEnabledActions({
	methods,
	isDisableLocked,
	onRecoveryCodes,
}: {
	methods: MfaMethod[];
	isDisableLocked: boolean;
	onRecoveryCodes: (recoveryCodes: string[]) => void;
}) {
	const { t } = useTranslation("frontend");
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [isDisabling, setIsDisabling] = useState(false);

	if (isRegenerating) {
		return (
			<RegenerateRecoveryCodesForm
				onDone={(codes) => {
					setIsRegenerating(false);
					onRecoveryCodes(codes);
				}}
				onCancel={() => setIsRegenerating(false)}
			/>
		);
	}

	if (isDisabling) {
		return (
			<DisableMethodForm
				method="totp"
				methods={methods}
				onDone={() => setIsDisabling(false)}
			/>
		);
	}

	return (
		<div className="flex gap-2">
			<Button variant="outline" onClick={() => setIsRegenerating(true)}>
				{t("accountSettings.mfa.regenerate")}
			</Button>
			{isDisableLocked ? (
				<SuperAdminLastMethodNotice />
			) : (
				<Button variant="outline" onClick={() => setIsDisabling(true)}>
					{t("accountSettings.mfa.disable")}
				</Button>
			)}
		</div>
	);
}

function SuperAdminLastMethodNotice() {
	const { t } = useTranslation("frontend");

	return (
		<p className="text-xs text-muted-foreground">
			{t("accountSettings.mfa.superAdminLastMethod")}
		</p>
	);
}

function ToggleableDisableForm({
	method,
	methods,
	isDisableLocked,
}: {
	method: MfaMethod;
	methods: MfaMethod[];
	isDisableLocked: boolean;
}) {
	const { t } = useTranslation("frontend");
	const [isDisabling, setIsDisabling] = useState(false);

	if (isDisableLocked) {
		return <SuperAdminLastMethodNotice />;
	}

	if (!isDisabling) {
		return (
			<Button
				variant="outline"
				className="w-fit"
				onClick={() => setIsDisabling(true)}
			>
				{t("accountSettings.mfa.disable")}
			</Button>
		);
	}

	return (
		<DisableMethodForm
			method={method}
			methods={methods}
			onDone={() => setIsDisabling(false)}
		/>
	);
}

function RegenerateRecoveryCodesForm({
	onDone,
	onCancel,
}: {
	onDone: (recoveryCodes: string[]) => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation("frontend");
	const [code, setCode] = useState("");
	const regenerate = useRegenerateMfaRecoveryCodes();

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		const { recoveryCodes } = await regenerate.mutateAsync({ code });
		onDone(recoveryCodes);
	};

	return (
		<form
			onSubmit={(event) => void handleSubmit(event).catch(() => setCode(""))}
			className="flex flex-col gap-3"
		>
			<p className="text-sm">{t("accountSettings.mfa.regenerateDescription")}</p>
			<Label htmlFor="mfa-regenerate-code">{t("accountSettings.mfa.code")}</Label>
			<SixDigitCodeInput
				id="mfa-regenerate-code"
				value={code}
				onChange={setCode}
			/>
			<ErrorMessage error={regenerate.error} />
			<div className="flex gap-2">
				<Button
					type="submit"
					disabled={!SIX_DIGIT_CODE_PATTERN.test(code) || regenerate.isPending}
				>
					{regenerate.isPending ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						t("misc.confirm")
					)}
				</Button>
				<Button type="button" variant="ghost" onClick={onCancel}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}

/** Proven by the password and a code from any enabled method. */
function DisableMethodForm({
	method,
	methods,
	onDone,
}: {
	method: MfaMethod;
	methods: MfaMethod[];
	onDone: () => void;
}) {
	const { t } = useTranslation("frontend");
	const [password, setPassword] = useState("");
	const [proof, setProof] = useState<Proof>({
		method: methods[0] ?? method,
		value: "",
	});
	const disable = useDisableMfa();

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		await disable.mutateAsync({ method, password, ...toMfaProof(proof) });
		onDone();
	};

	return (
		<form
			onSubmit={(event) =>
				void handleSubmit(event).catch(() =>
					setProof((previous) => ({ ...previous, value: "" })),
				)
			}
			className="flex flex-col gap-3"
		>
			<p className="text-sm">
				{t("accountSettings.mfa.disableMethodDescription")}
			</p>
			<Label htmlFor={`mfa-disable-password-${method}`}>
				{t("accountSettings.currentPassword")}
			</Label>
			<Input
				id={`mfa-disable-password-${method}`}
				type="password"
				value={password}
				onChange={(event) => setPassword(event.target.value)}
				autoComplete="current-password"
				autoFocus
			/>
			<ProofField
				id={`mfa-disable-code-${method}`}
				methods={methods}
				proof={proof}
				onChange={setProof}
			/>
			<ErrorMessage error={disable.error} />
			<div className="flex gap-2">
				<Button
					type="submit"
					variant="destructive"
					disabled={!proof.value.trim() || !password || disable.isPending}
				>
					{disable.isPending ? <Loader2 className="size-4 animate-spin" /> : t("misc.confirm")}
				</Button>
				<Button type="button" variant="ghost" onClick={onDone}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}
